'use client';

import { useCallback, useEffect, useState } from 'react';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import {
  type ChannelMemberItem,
  fetchChannelMembers,
} from '../functions/members/fetchChannelMembers';

interface State {
  members: ChannelMemberItem[];
  isLoading: boolean;
  error: Error | undefined;
  /** Re-fetch the current (channelId, blocked) list — e.g. after a member is
   *  blocked/unblocked so it hops between the All and Blocked tabs. */
  refresh: () => void;
}

const kInitial = {
  members: [] as ChannelMemberItem[],
  isLoading: true,
  error: undefined as Error | undefined,
};

/**
 * Members list for a channel — fetched once per (channelId,
 * blocked) tuple. Slice 4 doesn't subscribe to live changes; the
 * panel is opened on-demand and stays modal, so a one-shot fetch
 * is enough. Toggling the All/Blocked tab re-fetches, as does an
 * explicit {@link State.refresh} after a moderation action.
 */
export function useChannelMembers(
  channelId: string | null | undefined,
  { blocked }: { blocked?: boolean } = {},
): State {
  const { creatorId } = useCommunityContext();
  const [state, setState] = useState(kInitial);
  const [reloadIndex, setReloadIndex] = useState(0);
  const refresh = useCallback(() => setReloadIndex(index => index + 1), []);

  useEffect(() => {
    if (!creatorId || !channelId) {
      setState(kInitial);
      return;
    }
    let cancelled = false;
    setState({ members: [], isLoading: true, error: undefined });
    void fetchChannelMembers(creatorId, channelId, { blocked })
      .then(members => {
        if (cancelled) return;
        setState({ members, isLoading: false, error: undefined });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          members: [],
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [creatorId, channelId, blocked, reloadIndex]);

  return { ...state, refresh };
}
